import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebounce } from '@shared/hooks/useDebounce';
import { listSubmittedForms } from '@epics/taskFormManagement/api/listSubmittedFormsApiService';
import type { SubmittedForm } from '@epics/taskFormManagement/types';
import type { DateRange } from '@shared/components/FilterPopup';

const PAGE_SIZE = 10;

export const useSubmittedFormList = () => {
  const [forms, setForms] = useState<SubmittedForm[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [isNoFilterFetch, setIsNoFilterFetch] = useState(false);

  const [search, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const [dateRange, setDateRange] = useState<DateRange>({});
  const [appliedFilters, setAppliedFilters] = useState<{
    dateRange: DateRange;
  }>({
    dateRange: {},
  });

  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<() => void>(() => {});

  const hasActiveFilter =
    !!appliedFilters.dateRange.startDate || !!appliedFilters.dateRange.endDate;
  const hasActiveSearch = !!debouncedSearch.trim();

  // Reset list when search or filters change
  useEffect(() => {
    setForms([]);
    setPage(1);
    setHasNextPage(false);
    setFetchError('');
  }, [debouncedSearch, appliedFilters]);

  // Fetch page of results
  useEffect(() => {
    let cancelled = false;

    const fetch = async () => {
      setLoading(true);
      setFetchError('');

      const result = await listSubmittedForms({
        page,
        limit: PAGE_SIZE,
        ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
        ...(appliedFilters.dateRange.startDate && {
          startDate: appliedFilters.dateRange.startDate,
        }),
        ...(appliedFilters.dateRange.endDate && {
          endDate: appliedFilters.dateRange.endDate,
        }),
      });

      if (cancelled) {
        return;
      }

      if (!result.success || !result.data) {
        setFetchError(result.error ?? '');
        setLoading(false);
        return;
      }

      const { data, pagination } = result.data;
      setForms((prev) => (page === 1 ? data : [...prev, ...data]));
      setHasNextPage(pagination.hasNextPage ?? false);
      setIsNoFilterFetch(!debouncedSearch.trim() && !hasActiveFilter);
      setLoading(false);
    };

    void fetch();

    return () => {
      cancelled = true;
    };
    // hasActiveFilter is derived from appliedFilters — including both would double-trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, appliedFilters]);

  // Keep loadMoreRef current to avoid stale closures in the observer callback
  const loadMore = useCallback(() => {
    if (!loading && hasNextPage) {
      setPage((prev) => prev + 1);
    }
  }, [loading, hasNextPage]);

  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  // Infinite scroll — root is the inner scroll container, not the viewport
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = scrollContainerRef.current;
    if (!sentinel || !container) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreRef.current();
        }
      },
      { root: container, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage]);

  const setSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({ dateRange });
    setPage(1);
  }, [dateRange]);

  const handleClearFilters = useCallback(() => {
    setDateRange({});
    setAppliedFilters({ dateRange: {} });
    setPage(1);
  }, []);

  return {
    forms,
    loading,
    hasNextPage,
    fetchError,
    isNoFilterFetch,
    hasActiveSearch,
    hasActiveFilter,
    search,
    setSearch,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    sentinelRef,
    scrollContainerRef,
  };
};
